import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressBarModule } from 'primeng/progressbar';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { OverlayModule } from 'primeng/overlay';
import { ToastMessageService } from '@fitness-force/api';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ClientMilestoneService,
  MilestoneConfigService,
  ClientMilestoneProgressItem,
  ClientMilestoneCardData,
  ClientMilestoneSummary,
  GetMilestoneConfigResponse,
  ClientMilestoneAchievementResponsePagedResults
} from '@fitness-force/milestone';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';

@Component({
  selector: 'app-client-milestone-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    ProgressBarModule,
    DialogModule,
    SelectModule,
    DatePickerModule,
    InputNumberModule,
    OverlayModule
  ],
  templateUrl: './client-milestone-tab.component.html',
  styleUrls: ['./client-milestone-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientMilestoneTabComponent implements OnInit, OnDestroy {
  private readonly clientMilestoneService = inject(ClientMilestoneService);
  private readonly milestoneConfigService = inject(MilestoneConfigService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly clientId = input.required<number>();
  readonly tenantId = input<number>(0);

  loading = signal<boolean>(false);
  recomputeLoading = signal<boolean>(false);
  milestoneData = signal<ClientMilestoneProgressItem[]>([]);
  achievementsData = signal<ClientMilestoneAchievementResponsePagedResults | null>(null);
  showOverrideDialog = signal<boolean>(false);
  showRevokeDialog = signal<boolean>(false);
  companyId = signal<number>(0);
  
  availableMilestones = signal<GetMilestoneConfigResponse[]>([]);
  awardLoading = signal<boolean>(false);
  revokeLoading = signal<boolean>(false);
  selectedMilestoneToRevoke = signal<ClientMilestoneCardData | null>(null);
  activeOverlayMilestoneId = signal<number | null>(null);
  
  awardForm = new FormGroup({
    milestoneConfigId: new FormControl<number | null>(null, Validators.required),
    achievedValue: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
    achievedDate: new FormControl<Date>(new Date(), Validators.required)
  });

  revokeForm = new FormGroup({
    reason: new FormControl<string>('', [Validators.required, Validators.minLength(10)])
  });

  toggleMilestoneOverlay(milestoneId: number): void {
    if (this.activeOverlayMilestoneId() === milestoneId) {
      this.activeOverlayMilestoneId.set(null);
    } else {
      this.activeOverlayMilestoneId.set(milestoneId);
    }
  }

  closeMilestoneOverlay(): void {
    this.activeOverlayMilestoneId.set(null);
  }

  onRevokeClick(card: ClientMilestoneCardData): void {
    this.closeMilestoneOverlay();
    this.selectedMilestoneToRevoke.set(card);
    this.revokeForm.reset({ reason: '' });
    this.showRevokeDialog.set(true);
  }

  isOverlayVisible(milestoneId: number): boolean {
    return this.activeOverlayMilestoneId() === milestoneId;
  }

  summary = computed<ClientMilestoneSummary>(() => {
    const data = this.milestoneData();
    const achieved = data.filter(m => m.isAchieved).length;
    const inProgress = data.filter(m => !m.isAchieved && m.currentValue > 0).length;
    const notStarted = data.filter(m => !m.isAchieved && m.currentValue === 0).length;
    
    return {
      achieved,
      inProgress,
      notStarted,
      total: data.length
    };
  });

  milestoneCards = computed<ClientMilestoneCardData[]>(() => {
    return this.milestoneData().map(item => this.transformToCardData(item));
  });

  constructor() {
    this.setupTenantSubscription();
  }

  ngOnInit(): void {
    this.loadClientMilestoneProgress();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupTenantSubscription(): void {
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(tenant => {
        if (tenant?.selected_tenant?.company_id) {
          this.companyId.set(tenant.selected_tenant.company_id);
        }
      });
  }

  loadClientMilestoneProgress(): void {
    this.loading.set(true);
    
    forkJoin({
      progress: this.clientMilestoneService.getClientMilestoneProgress(this.clientId()),
      achievements: this.clientMilestoneService.getClientMilestoneAchievements(this.clientId())
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.achievementsData.set(response.achievements);
          
          // Merge achievement IDs into milestone data
          const progressWithAchievementIds = (response.progress.milestones || []).map(milestone => {
            const achievement = response.achievements.items.find(
              a => a.milestoneConfigId === milestone.milestoneConfigId
            );
            return {
              ...milestone,
              achievementId: achievement?.id,
              isAchieved: !!achievement,
              achievedDate: achievement?.achievedDate
            };
          });
          
          this.milestoneData.set(progressWithAchievementIds);
          this.loading.set(false);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.loading.set(false);
        }
      });
  }

  private transformToCardData(item: ClientMilestoneProgressItem): ClientMilestoneCardData {
    const progressPercentage = item.targetValue > 0 
      ? Math.min(Math.round((item.currentValue / item.targetValue) * 100), 100)
      : 0;

    let status: 'achieved' | 'in-progress' | 'not-started' = 'not-started';
    let statusLabel = this.translate.instant('CLIENT_MILESTONE.STATUS.NOT_STARTED');

    if (item.isAchieved) {
      status = 'achieved';
      statusLabel = this.translate.instant('CLIENT_MILESTONE.STATUS.ACHIEVED');
    } else if (item.currentValue > 0) {
      status = 'in-progress';
      statusLabel = `${progressPercentage}%`;
    }

    const description = this.buildDescription(item);
    const rewardIcon = this.getRewardIcon(item.rewardType);

    return {
      milestoneConfigId: item.milestoneConfigId,
      name: item.name || this.translate.instant('CLIENT_MILESTONE.UNNAMED'),
      tierName: item.tierName,
      description,
      milestoneType: item.milestoneType || '',
      targetValue: item.targetValue,
      currentValue: item.currentValue,
      unit: item.unit,
      periodType: item.periodType,
      rewardType: item.rewardType || '',
      rewardIcon,
      progressPercentage,
      status,
      statusLabel,
      achievedDate: item.achievedDate,
      achievementId: item.achievementId,
      isLocked: !item.isAchieved
    };
  }

  private buildDescription(item: ClientMilestoneProgressItem): string {
    const target = `${item.targetValue} ${item.unit || ''}`;
    const typeLabel = item.milestoneType ? 
      this.translate.instant(`CLIENT_MILESTONE.TYPE.${item.milestoneType.toUpperCase()}`) : '';
    const periodLabel = item.periodType ? 
      this.translate.instant(`CLIENT_MILESTONE.PERIOD.${item.periodType.toUpperCase()}`) : '';
    
    return `${typeLabel} ${target} ${periodLabel}`.trim();
  }

  private getRewardIcon(rewardType: string | undefined): string {
    switch (rewardType?.toLowerCase()) {
      case 'badge':
        return 'pi pi-shield';
      case 'points':
      case 'wallet_credit':
        return 'pi pi-wallet';
      case 'discount':
        return 'pi pi-percentage';
      case 'free_session':
        return 'pi pi-calendar';
      default:
        return 'pi pi-gift';
    }
  }

  getRewardLabel(rewardType: string): string {
    const key = `CLIENT_MILESTONE.REWARD.${rewardType.toUpperCase()}`;
    return this.translate.instant(key);
  }

  handleRecompute(): void {
    const milestoneConfigIds = [...new Set(this.milestoneData().map(m => m.milestoneConfigId))];
    
    if (milestoneConfigIds.length === 0) {
      this.toastMessageService.showInfo(
        this.translate.instant('CLIENT_MILESTONE.MESSAGES.NO_MILESTONES_TO_RECOMPUTE')
      );
      return;
    }

    this.recomputeLoading.set(true);
    
    const recalculateRequests = milestoneConfigIds.map(configId =>
      this.milestoneConfigService.forceRecalculate(configId, { clientIds: [this.clientId()] })
    );

    forkJoin(recalculateRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          const totalProcessed = responses.reduce((sum, r) => sum + r.clientsProcessed, 0);
          this.toastMessageService.showSuccess(
            this.translate.instant('CLIENT_MILESTONE.MESSAGES.RECOMPUTE_SUCCESS', { count: totalProcessed })
          );
          this.recomputeLoading.set(false);
          this.loadClientMilestoneProgress();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.recomputeLoading.set(false);
        }
      });
  }

  handleOverrideAward(): void {
    this.loadAvailableMilestones();
    this.showOverrideDialog.set(true);
    this.cdr.markForCheck();
  }

  closeOverrideDialog(): void {
    this.showOverrideDialog.set(false);
    this.awardForm.reset({
      milestoneConfigId: null,
      achievedValue: 0,
      achievedDate: new Date()
    });
  }

  private loadAvailableMilestones(): void {
    this.milestoneConfigService.listMilestoneConfigurations({ isActive: true, pageSize: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableMilestones.set(response.items || []);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
        }
      });
  }

  onMilestoneSelected(event: any): void {
    const selectedMilestone = this.availableMilestones().find(m => m.id === event.value);
    if (selectedMilestone) {
      this.awardForm.patchValue({
        achievedValue: selectedMilestone.targetValue
      });
    }
  }

  submitAwardMilestone(): void {
    if (this.awardForm.invalid) {
      const message = this.translate.instant('CLIENT_MILESTONE.OVERRIDE_AWARD.VALIDATION_ERROR');
      this.toastMessageService.showInfo(message);
      return;
    }

    const formValue = this.awardForm.getRawValue();
    
    if (!formValue.milestoneConfigId || formValue.achievedValue === null || !formValue.achievedDate) {
      const message = this.translate.instant('CLIENT_MILESTONE.OVERRIDE_AWARD.VALIDATION_ERROR');
      this.toastMessageService.showInfo(message);
      return;
    }

    const request = {
      milestoneConfigId: formValue.milestoneConfigId,
      achievedValue: formValue.achievedValue,
      achievedDate: formValue.achievedDate.toISOString()
    };

    this.awardLoading.set(true);
    this.clientMilestoneService.manuallyAwardMilestone(this.clientId(), request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const message = this.translate.instant('CLIENT_MILESTONE.OVERRIDE_AWARD.SUCCESS');
          this.toastMessageService.showSuccess(message);
          this.closeOverrideDialog();
          this.loadClientMilestoneProgress();
          this.awardLoading.set(false);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.awardLoading.set(false);
        }
      });
  }

  handleRevokeMilestone(milestone: ClientMilestoneCardData): void {
    this.selectedMilestoneToRevoke.set(milestone);
    this.revokeForm.reset({ reason: '' });
    this.showRevokeDialog.set(true);
    this.cdr.markForCheck();
  }

  closeRevokeDialog(): void {
    this.showRevokeDialog.set(false);
    this.selectedMilestoneToRevoke.set(null);
    this.revokeForm.reset();
  }

  submitRevokeMilestone(): void {
    if (this.revokeForm.invalid) {
      const message = this.translate.instant('CLIENT_MILESTONE.REVOKE.VALIDATION_ERROR');
      this.toastMessageService.showInfo(message);
      return;
    }

    const milestone = this.selectedMilestoneToRevoke();
    if (!milestone) {
      return;
    }

    const formValue = this.revokeForm.getRawValue();
    if (!formValue.reason) {
      const message = this.translate.instant('CLIENT_MILESTONE.REVOKE.VALIDATION_ERROR');
      this.toastMessageService.showInfo(message);
      return;
    }

    this.revokeLoading.set(true);
    
    // Find the achievement ID from the merged milestone data
    const milestoneAchievement = this.milestoneData().find(
      m => m.milestoneConfigId === milestone.milestoneConfigId && m.achievementId
    );
    
    if (!milestoneAchievement?.achievementId) {
      const message = this.translate.instant('CLIENT_MILESTONE.REVOKE.NO_ACHIEVEMENT_FOUND');
      this.toastMessageService.showErrorMessage(message);
      this.revokeLoading.set(false);
      return;
    }

    this.clientMilestoneService.revokeClientMilestoneAchievement(this.clientId(), milestoneAchievement.achievementId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const message = this.translate.instant('CLIENT_MILESTONE.REVOKE.SUCCESS');
          this.toastMessageService.showSuccess(message);
          this.closeRevokeDialog();
          this.loadClientMilestoneProgress();
          this.revokeLoading.set(false);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.revokeLoading.set(false);
        }
      });
  }

  getSummaryIcon(type: 'achieved' | 'in-progress' | 'not-started' | 'total'): string {
    switch (type) {
      case 'achieved':
        return 'pi pi-check-circle';
      case 'in-progress':
        return 'pi pi-chart-line';
      case 'not-started':
        return 'pi pi-lock';
      case 'total':
        return 'pi pi-trophy';
      default:
        return 'pi pi-circle';
    }
  }
}
